import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { IamDivision } from '@r1-compass-library/models/iam/iam-division.model';

export type DivisionDialogData = {
    iamDivision?: Partial<IamDivision>;
    description?: string;
};

export type AddDivisionDialogForm = {
    divisionCode: FormControl<string>;
    description: FormControl<string | undefined>
};
export type AddDivisionDialogFormData = {
    divisionCode: string;
    description?: string;
};

@Component({
    selector: 'r1-compass-app-add-division-dialog',
    imports: [MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
    template: `
        <div id="dialog-wrapper">
            <h2>
                @if (data.iamDivision) {
                    Edit Division
                } @else {
                    New Division
                }
            </h2>
            <p>
                Enter an abbreviation and an optional description for the division. The abbreviation will be used to reference when
                interacting with the Data Mart.
            </p>
            <mat-dialog-content>
                <form [formGroup]="form">
                    <mat-form-field>
                        <mat-label>Division Abbreviation</mat-label>
                        <input matInput type="text" [formControl]="form.controls.divisionCode" />
                        @if (form.controls.divisionCode.errors) {
                            <mat-error>Abbreviation is required.</mat-error>
                        }
                    </mat-form-field>
                    <mat-form-field>
                        <mat-label>Description</mat-label>
                        <input matInput type="text" [formControl]="form.controls.description" />
                        @if (form.controls.description.errors) {
                            <mat-error>Max length 50 characters</mat-error>
                        }
                    </mat-form-field>
                </form>
            </mat-dialog-content>
            <mat-dialog-actions>
                <button mat-stroked-button (click)="onClose()" type="button">Cancel</button>
                <button
                    mat-flat-button
                    color="primary"
                    (click)="onClose(form.getRawValue())"
                    [disabled]="!form.valid"
                    type="button">
                    Done
                </button>
            </mat-dialog-actions>
        </div>
    `,
    styles: `
        #dialog-wrapper {
            padding: 0.5rem;
        }
        form {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
        mat-dialog-actions {
            display: flex;
            justify-content: flex-end;
        }
    `,
})
export class AddDivisionDialogComponent {
    readonly #formBuilder = inject(FormBuilder);
    dialogRef = inject<MatDialogRef<AddDivisionDialogComponent>>(MatDialogRef);
    data = inject<DivisionDialogData>(MAT_DIALOG_DATA);

    form: FormGroup<AddDivisionDialogForm> = this.#formBuilder.group({
        divisionCode: this.#formBuilder.control<string>('', { validators: Validators.required, nonNullable: true }),
        description: this.#formBuilder.control<string | undefined>(undefined, { validators: Validators.maxLength(50), nonNullable: true })
    });

    constructor() {
        const data = this.data;

        this.form.controls.divisionCode.setValue(data.iamDivision?.divisionCode ?? '');
        this.form.controls.description.setValue(data.iamDivision?.description)
    }

    onClose(value?: AddDivisionDialogFormData) {
        this.dialogRef.close(value);
    }
}
